import { useLocation } from '@reach/router';
import React from 'react';
import JsonLd from '../utils/json-ld';
import Container from '../atoms/Container';
import StyledLinkUnderline from '../atoms/StyledLinkUnderline';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import Link from '../primitives/link';
import { colors } from '../primitives/colors';

interface FranchiseTopNav {
    currentPageTitle: string
}

const FranchiseTopNav = ({ currentPageTitle }: FranchiseTopNav): JSX.Element => {
    const { name, permalink } = useMicrosite();
    const { pathname } = useLocation();

    const memoizedBreadcrumb = React.useMemo(() => {
        const formattedPermalink = `/${(permalink || '').split('/').filter(x => !!x).join('/')}`;
        const routeSegments = pathname.replace(permalink || '', '').split('/').filter(x => !!x).filter(x => x !== 'locations');

        /*
        * This route map should be updated in future to include
        * specific sub-category lines
        * */
        const routeMap: { [k: string]: string } = {
            // services: 'Services',
        };

        const breadcrumbs: { desktopTitle: string, mobileTitle: string, url: string | null }[] = [];

        if (routeSegments.length === 0 && name) {
            breadcrumbs.push({
                desktopTitle: name,
                mobileTitle: '',
                url: null,
            });
            breadcrumbs.push({
                desktopTitle: 'Home',
                mobileTitle: 'Home',
                url: null,
            });
        } else if (name) {
            breadcrumbs.push({
                desktopTitle: name,
                mobileTitle: 'Home',
                url: formattedPermalink,
            });
        }

        routeSegments.forEach((segment) => {
            if (routeMap[segment]) {
                breadcrumbs.push({
                    desktopTitle: routeMap[segment],
                    mobileTitle: routeMap[segment],
                    url: [formattedPermalink, segment].join('/'),
                });
            }
        });

        if (routeSegments.length !== 0) {
            breadcrumbs.push({
                desktopTitle: currentPageTitle,
                mobileTitle: currentPageTitle,
                url: null,
            });
        }

        return breadcrumbs;
    }, [pathname, permalink, currentPageTitle, name]);

    return (
        <div css={styles.topBarWrap}>
            <Container>
                <div css={styles.topBar}>
                    <JsonLd>
                        {{
                            '@context': 'https://schema.org',
                            '@type': 'BreadcrumbList',
                            itemListElement: memoizedBreadcrumb.map(({ desktopTitle, url }, idx) => ({
                                '@type': 'ListItem',
                                position: idx + 1,
                                name: desktopTitle,
                                item: url === null ? `https://servpro.com${pathname}` : `https://servpro.com${url}`,
                            })),
                        }}
                    </JsonLd>
                    {memoizedBreadcrumb.map(({ desktopTitle, mobileTitle, url }, idx) => (
                        <React.Fragment key={url || ''}>
                            <div css={styles.breadcrumbWrap}>
                                {url === null ? (
                                    <div
                                        css={styles.desktopTitle}
                                    >
                                        {desktopTitle}
                                    </div>
                                ) : (
                                    <StyledLinkUnderline
                                        css={styles.desktopTitle}
                                        to={url}
                                    >
                                        {desktopTitle}
                                    </StyledLinkUnderline>
                                )}
                                {(idx === 0 && url !== null && mobileTitle) && (
                                    <Link
                                        css={styles.mobileTitleBase}
                                        to={url}
                                    >
                                        {mobileTitle}
                                    </Link>
                                )}
                                {(idx !== 0 && url !== null) && (
                                    <StyledLinkUnderline
                                        css={styles.mobileTitle}
                                        to={url}
                                    >
                                        {mobileTitle}
                                    </StyledLinkUnderline>
                                )}
                                {(idx !== 0 && url === null) && (
                                    <div
                                        css={styles.mobileTitle}
                                    >
                                        {mobileTitle}
                                    </div>
                                )}
                            </div>
                            {idx === 0 && (
                                <span css={styles.desktopTitle}>›</span>
                            )}
                            {(idx !== 0 && idx !== memoizedBreadcrumb.length - 1) && (<span>›</span>)}
                        </React.Fragment>
                    ))}
                    <div css={styles.gradientEnd} />
                </div>
            </Container>
        </div>
    );
};

const styles = {
    rightPointingMark: {

    },
    mobileTitle: {
        '@media (min-width: 1024px)': {
            display: 'none',
        },
    },
    mobileTitleBase: {
        backgroundColor: '#0001',
        padding: '6px 10px',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '3px',
        gap: '8px',
        '@media (min-width: 1024px)': {
            display: 'none',
        },
    },
    desktopTitle: {
        display: 'none',
        '@media (min-width: 1024px)': {
            display: 'unset',
        },
    },
    breadcrumbWrap: {
        display: 'inline',
        flex: 'none',
        whiteSpace: 'nowrap',
    },
    gradientEnd: {
        background: 'linear-gradient(90deg, rgba(255,102,0,0) 0%, rgba(255,102,0,1) 100%)',
        position: 'absolute',
        right: 0,
        top: 0,
        height: '100%',
        width: '60px',
    },
    topBarWrap: {
        backgroundColor: colors.primaryOrange,
        overflow: 'hidden',
        position: 'relative',
        paddingTop: '0.75rem',
        paddingBottom: '0.75rem',
        fontSize: '0.75rem',
        lineHeight: '1rem',
        color: colors.shadesWhite,
        '@media (min-width: 1024px)': {
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
        },
    },
    topBar: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        width: '100%',
        gap: '8px',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
};

export default FranchiseTopNav;
